import moment from "moment";

import {getSinglePortfolio} from "#app/api";
import {crmClientPlatformUserId} from "#app/api/clients/crmClientPlatformUserId";
import type {Client} from "#app/api/types/api.types";
import type {ApiRawRecord} from "#app/api/types/common";

type ClientId = string | number;

type PortfolioCacheEntry = {
    accounts: any[];
    portfolioDate: string | null;
    loadedAt: number;
};

const portfolioCache = new Map<ClientId, PortfolioCacheEntry>();
const inFlightRequests = new Map<ClientId, Promise<PortfolioCacheEntry>>();

type PortfolioItem = { date?: string };
type PortfolioGroup = { items?: PortfolioItem[] };
type PortfolioAccount = { groups?: PortfolioGroup[] };

const getPortfolioDate = (accounts: any[]): string | null => {
    const firstAccount = accounts?.[0] as PortfolioAccount | undefined;
    const firstGroup = firstAccount?.groups?.[0];
    const firstItem = firstGroup?.items?.[0];
    const rawDate = firstItem?.date;
    if (!rawDate) return null;

    const parsedDate = moment(rawDate, ["DD.MM.YYYY HH:mm:ss", "YYYY-MM-DD"], true);
    return parsedDate.isValid() ? parsedDate.format("DD.MM.YYYY") : null;
};

type PortfolioLoadOptions = {
    client?: ApiRawRecord | null;
};

function resolveClientOwnId(client: ApiRawRecord | {id: ClientId} | null | undefined): ClientId | null {
    const rawId = (client as {id?: ClientId} | null | undefined)?.id;
    if (rawId == null) return null;
    const numericId = Number(rawId);
    return Number.isFinite(numericId) && numericId > 0 ? numericId : rawId;
}

/**
 * Портфолио (`getSinglePortfolio` → `indexByUserId`) исторически грузится по CRM `client.id` —
 * это рабочий id, по которому портфели отдавались до правок funnel-слоя.
 * Платформенный `mainContact.user_id` — только резерв, если PK почему-то отсутствует.
 * Логика намеренно независима от funnel-scope: там собственные правила сопоставления id.
 */
export function resolvePortfolioApiUserId(
    client: ApiRawRecord | {id: ClientId} | null | undefined
): ClientId | null {
    const ownId = resolveClientOwnId(client);
    if (ownId != null) {
        return ownId;
    }
    const platformUserId = client ? crmClientPlatformUserId(client as Client) : undefined;
    return platformUserId ?? null;
}

function resolvePortfolioApiUserIdForFetch(
    clientId: ClientId,
    options?: PortfolioLoadOptions
): ClientId | null {
    return resolvePortfolioApiUserId(options?.client ?? {id: clientId});
}

const loadPortfolioAccounts = (
    clientId: ClientId,
    options?: PortfolioLoadOptions
): Promise<any[]> => {
    const apiUserId = resolvePortfolioApiUserIdForFetch(clientId, options);
    if (apiUserId == null) {
        return Promise.resolve([]);
    }
    return new Promise((resolve) => {
        getSinglePortfolio(apiUserId, process.env.REACT_APP_API_WHITESWAN_URL, (accounts: any[]) => {
            resolve(accounts ?? []);
        });
    });
};

const createCacheEntry = (accounts: any[]): PortfolioCacheEntry => ({
    accounts,
    portfolioDate: getPortfolioDate(accounts),
    loadedAt: Date.now(),
});

export const getCachedPortfolioByClient = (clientId: ClientId): PortfolioCacheEntry | null =>
    portfolioCache.get(clientId) ?? null;

export const getPortfolioByClientCached = async (
    clientId: ClientId,
    options?: { force?: boolean; client?: ApiRawRecord | null }
): Promise<PortfolioCacheEntry> => {
    const shouldForceReload = options?.force === true;

    if (!shouldForceReload) {
        const cached = portfolioCache.get(clientId);
        if (cached) return cached;

        const inFlight = inFlightRequests.get(clientId);
        if (inFlight) return inFlight;
    }

    const request = loadPortfolioAccounts(clientId, {
        client: options?.client,
    })
        .then((accounts) => {
            const cacheEntry = createCacheEntry(accounts);
            portfolioCache.set(clientId, cacheEntry);
            return cacheEntry;
        })
        .finally(() => {
            inFlightRequests.delete(clientId);
        });

    inFlightRequests.set(clientId, request);
    return request;
};

export const clearPortfolioByClientSessionCache = () => {
    portfolioCache.clear();
    inFlightRequests.clear();
};

type PrefetchableClient = ApiRawRecord & { id?: ClientId };

/**
 * Фоновая загрузка портфелей всех клиентов менеджера в session-кэш.
 * Открытие модалки портфолио → мгновенно из кэша. Лимит параллельных запросов, чтобы не душить WS API.
 */
export const prefetchPortfoliosForClients = (
    clients: ReadonlyArray<PrefetchableClient>,
    options?: { concurrency?: number }
): void => {
    const concurrencyLimit = Math.max(1, options?.concurrency ?? 4);
    const pendingClientsList = clients.filter((clientEntry): clientEntry is PrefetchableClient & { id: ClientId } => {
        const clientId = clientEntry?.id;
        if (clientId == null) {
            return false;
        }
        if (portfolioCache.has(clientId) || inFlightRequests.has(clientId)) {
            return false;
        }
        return resolvePortfolioApiUserId(clientEntry) != null;
    });

    let cursorIndex = 0;
    const runNextPrefetch = (): void => {
        if (cursorIndex >= pendingClientsList.length) {
            return;
        }
        const clientEntry = pendingClientsList[cursorIndex];
        cursorIndex += 1;
        void getPortfolioByClientCached(clientEntry.id, { client: clientEntry }).finally(runNextPrefetch);
    };

    for (let workerIndex = 0; workerIndex < Math.min(concurrencyLimit, pendingClientsList.length); workerIndex += 1) {
        runNextPrefetch();
    }
};
